<template>
  <template v-if="$route.name === 'teachers.index'">
    <TeacherFilter 
      @search="handleSearch" 
      @gender="handleGenderFilter"
      @add="navigateToAdd"
      @reset-filters="handleResetFilters"
    />
    
    <div class="card-body">
      <TeacherTable
        :teachers="teachers"
        :loading="isLoading"
        @edit-teacher="navigateToEdit"
        @delete-teacher="openDeleteDialog"
      />
    </div>
    
   <PaginationControls
  v-if="!isLoading && pagination.totalPages > 1"
  :pagination="pagination"
  @page-changed="handlePageChange"
  @per-page-changed="handlePerPageChange"
/>

    <DeleteTeacherDialog ref="deleteDialog" @delete="handleDeleteTeacher" />
  </template>
  <router-view></router-view>
</template>
<script setup>
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import TeacherTable from "@/components/features/teachers/TeacherTable.vue";
import TeacherFilter from "@/components/features/teachers/filters/TeacherFilter.vue";
import PaginationControls from "@/components/common/PaginationControls.vue";
import DeleteTeacherDialog from "@/components/features/teachers/dialogs/DeleteTeacherDialog.vue";

const { t, locale } = useI18n();
const store = useStore();
const router = useRouter();
const toast = useToast();

/* ------------ refs ------------ */
const deleteDialog = ref(null);
const isLoading = ref(false);

/* ------------ computed ------------ */
const teachers = computed(() => store.state.teacher.teachers);
const pagination = computed(() => store.state.teacher.pagination);

/* ---------- helpers: consistent error toasts ---------- */
function extractApiMessage(error, fallbackKey = "errors.generic") {
  const data = error?.response?.data;
  if (data?.message) {
    if (typeof data.message === "object" && data.message !== null) {
      const loc = (data.locale || locale.value || "en")
        .toLowerCase()
        .startsWith("ar")
        ? "ar"
        : "en";
      if (typeof data.message[loc] === "string") return data.message[loc];
    }
    if (typeof data.message === "string") return data.message;
  }
  if (error?.response?.statusText) return error.response.statusText;
  return t(fallbackKey);
}

function notifyError(error, fallbackKey = "errors.generic") {
  const s = error?.response?.status;
  if (s === 401) return toast.error(t("auth.unauthorized", "Unauthorized"));
  if (s === 403) return toast.error(t("auth.forbidden", "Forbidden")); // explicit 403
  return toast.error(extractApiMessage(error, fallbackKey));
}

/* ------------ methods ------------ */
const fetchTeachers = async (page = 1, perPage = 10) => {
  isLoading.value = true;
  try {
    await store.dispatch("teacher/fetchTeachers", {
      page,
      per_page: perPage,
      search: store.state.teacher.filters.search,
      gender: store.state.teacher.filters.gender,
    });
  } catch (error) {
    notifyError(error, "teachers.load_failed"); // shows auth.forbidden on 403
    console.error("Error fetching teachers:", error);
  } finally {
    isLoading.value = false;
  }
};

const handleSearch = (searchTerm) => {
  store.commit("teacher/SET_FILTERS", {
    ...store.state.teacher.filters,
    search: searchTerm,
  });
  fetchTeachers(1);
};

const handleGenderFilter = (gender) => {
  store.commit("teacher/SET_FILTERS", {
    ...store.state.teacher.filters,
    gender,
  });
  fetchTeachers(1);
};

const handleResetFilters = () => {
  store.commit("teacher/SET_FILTERS", { search: "", gender: null });
  fetchTeachers(1);
};

const handlePageChange = (page) => {
  fetchTeachers(page, pagination.value.perPage);
};

const handlePerPageChange = (perPage) => {
  fetchTeachers(1, perPage);
};

const navigateToAdd = () => router.push({ name: "teachers.create" });
const navigateToEdit = (teacher) =>
  router.push({ name: "teachers.edit", params: { id: teacher.id } });

const openDeleteDialog = (teacher) => {
  deleteDialog.value?.open(teacher);
};

const handleDeleteTeacher = async (teacherId) => {
  try {
    // no global spinner here; keep the table calm
    const res = await store.dispatch("teacher/deleteTeacher", teacherId);

    // Normalize shapes:
    // A) store returns { ok, message }
    // B) raw axios-like response has status + data.message
    const ok =
      (typeof res?.ok === "boolean" && res.ok) ||
      (typeof res?.status === "number" && res.status >= 200 && res.status < 300) ||
      (res == null); // if action returns nothing but didn’t throw, treat as OK

    const message =
      res?.message || res?.data?.message || t("teachers.deleted_successfully");

    if (!ok) {
      toast.error(message || t("teachers.delete_failed"));
      return;
    }

    toast.success(message);

    // If current page got empty after deletion, go back one page if possible
    if (teachers.value.length === 0 && pagination.value.currentPage > 1) {
      await fetchTeachers(pagination.value.currentPage - 1, pagination.value.perPage);
    } else {
      // otherwise just refresh current page silently (optional)
      await fetchTeachers(pagination.value.currentPage, pagination.value.perPage);
    }
  } catch (error) {
    // store throws with a normalized message already
    const msg = error?.message || extractApiMessage(error, "teachers.delete_failed");
    toast.error(msg);
    console.error("Error deleting teacher:", error);
  }
};


/* ------------ lifecycle ------------ */
onMounted(() => fetchTeachers());
</script>
